/**
 * Единый реестр ключей TanStack Query (MVP_TECH_RFC §22).
 * Ключи объявляются здесь, чтобы инвалидация не расползалась по фичам.
 */
export const queryKeys = {
  config: () => ['config'] as const,
  me: () => ['me'] as const,
  // Гостевая сессия (FE-205): её состояние нужно оболочке приложения на каждом
  // экране, поэтому оно живёт в кеше, а не в контексте компонента.
  guestSession: () => ['guest-session'] as const,
  legalDocuments: () => ['legal-documents'] as const,
  // Локаль входит в ключ: от неё зависят alt-тексты изображений в ответе.
  vehicle: (slug: string, locale?: string) => ['vehicle', slug, locale ?? null] as const,
  // Ключуется по id, а не по slug: `/vehicles/{id}/provenance` принимает только id.
  vehicleProvenance: (vehicleId: string) => ['vehicle', vehicleId, 'provenance'] as const,
  // Список запросов ключуется фильтрами и сортировкой: они живут в URL и
  // задают выдачу, поэтому обязаны попадать в ключ кеша (MVP_PRD §20).
  searchSessions: (query: Record<string, unknown> = {}) => ['search-sessions', query] as const,
  searchSession: (id: string) => ['search-session', id] as const,
  searchRun: (id: string) => ['search-run', id] as const,
  // Тариф и состав услуги (FE-210): ключуется заказом — цена одна, а стадия и
  // размер рабочего набора у каждого заказа свои.
  paymentPreview: (searchId: string) => ['payment-preview', searchId] as const,
  // Состояние платежа (Epic 48): отдельный ключ от тарифа — тариф меняется
  // релизом, а статус платежа опрашивается, пока идёт подтверждение.
  paymentStatus: (searchId: string) => ['payment-status', searchId] as const,
  searchResults: (runId: string, filters: Record<string, unknown> = {}) =>
    ['search-run', runId, 'results', filters] as const,
  // Карточка ключуется параметрами списка: от них зависит `navigation`
  // («Auto 3 von 100», BE-580). Префикс без параметров — для инвалидации.
  searchResult: (runId: string, vehicleId: string, filters: Record<string, unknown> = {}) =>
    ['search-run', runId, 'result', vehicleId, filters] as const,
  // Четвёрка и её сравнение (Epic 22). Ключуются заказом: состав, история
  // слотов и таблица сравнения существуют только внутри него.
  selection: (searchId: string) => ['search-session', searchId, 'selection'] as const,
  comparison: (searchId: string) => ['search-session', searchId, 'comparison'] as const,
  // Заключение по сделке (Epic 25). Отдельный ключ, а не часть сравнения:
  // сравнение живёт на шаге 4 и меняется от ответов автохаусов, заключение —
  // на шаге 6 и меняется от предложений и финальной сверки.
  conclusion: (searchId: string) => ['search-session', searchId, 'conclusion'] as const,
  // Сопровождение покупки (Epic 26). Отдельный ключ, а не часть заказа: чек-лист
  // производный и меняется от ответов автохауса, а карточка заказа — от стадии.
  handover: (searchId: string) => ['search-session', searchId, 'handover'] as const,
  // Вознаграждение по результату (Epic 33). Отдельный ключ: расчёт появляется
  // только с отметкой исхода и меняется от подтверждения автохауса, а
  // сопровождение — от ответов и отметок чек-листа.
  successFee: (searchId: string) => ['search-session', searchId, 'success-fee'] as const,
  // Переговоры (Epic 17). Список ключуется фильтром по статусу: он живёт в
  // URL и задаёт выдачу. Карточка — идентификатором переписки: она переживает
  // удаление заказа (`ON DELETE SET NULL`, §23.10), поэтому под заказ не
  // ключуется.
  negotiations: (query: Record<string, unknown> = {}) => ['negotiations', query] as const,
  negotiation: (id: string) => ['negotiation', id] as const,
  // Профиль коммуникации автохауса (FE-240). Отдельный ключ, а не часть
  // карточки: карточка опрашивается раз в 2 секунды в окне отмены письма, а
  // профиль меняется только от ответа автохауса.
  negotiationMetrics: (id: string) => ['negotiation', id, 'metrics'] as const,
  // Предпросмотр запуска (BE-172): предусловия меняются извне — объявление
  // снимают, продавец отказывается от переписки, — поэтому не кэшируется.
  negotiationPreview: (vehicleId: string) => ['negotiation-preview', vehicleId] as const,
  // Каналы уведомлений (FE-160): состояние меняется извне — подтверждением в
  // боте, — поэтому ключ общий и инвалидируется после каждой мутации.
  notificationChannels: () => ['notification-channels'] as const,
  // Избранного отдельным списком нет: отметка — закладка на заказе (BE-581) и
  // приходит полем `is_favorite` в результатах прогона, под их ключами.
} as const;
